import { Controller } from "@hotwired/stimulus";
import { heir } from "../islands/campaign_hub/mesa";
import type { LiveScene } from "../types/generated/live";
import type MapBoard from "./map_board_controller";

type Entry = Pick<LiveScene, "id" | "name" | "active" | "version">;
type Labels = { failed: string; deleteBody: string; takesPlace: string; lastMap: string };

const fill = (text: string, name: string) => text.replace("%{name}", () => name);

// Connects to data-controller="map-scene-menu", beside map-board on the master's board root.
// The board's name opens this menu (the `menu` controller opens and closes it): the campaign's maps to go to, fetched each
// time it opens, then Configurações and Excluir for the open one. Excluir asks first, and the board leaves for the campaign
// once the map is gone.
export default class extends Controller<HTMLElement> {
	static targets = ["panel", "trigger", "more", "close", "view", "list", "row", "ask", "deleteBody", "confirm"];
	static values = { campaignUrl: String, labels: Object };
	declare readonly panelTarget: HTMLElement;
	declare readonly triggerTarget: HTMLElement;
	declare readonly moreTarget: HTMLElement;
	declare readonly closeTarget: HTMLElement;
	declare readonly viewTargets: HTMLElement[];
	declare readonly listTarget: HTMLElement;
	declare readonly rowTarget: HTMLTemplateElement;
	declare readonly askTarget: HTMLButtonElement;
	declare readonly deleteBodyTarget: HTMLElement;
	declare readonly confirmTarget: HTMLButtonElement;
	declare readonly campaignUrlValue: string;
	declare readonly labelsValue: Labels;
	scenes: Entry[] = [];
	loads = 0;
	hiding = new MutationObserver(() => this.panelClosed());

	connect() {
		document.addEventListener("keydown", this.claimEscape, true);
		this.hiding.observe(this.panelTarget, { attributes: true, attributeFilter: ["hidden"] });
	}

	disconnect() {
		document.removeEventListener("keydown", this.claimEscape, true);
		this.hiding.disconnect();
	}

	// The site's shortcuts skip a key already handled, so an Escape that dismisses the open menu never reaches the board's
	// own (a selection, a drawing, a tool). Capture runs first, whichever controller connected earlier. A dialog opened over
	// the menu owns its Escape.
	claimEscape = (event: KeyboardEvent) => {
		if (event.key === "Escape" && !this.panelTarget.hidden && !(event.target as Element).closest?.("dialog")) event.preventDefault();
	};

	// Below sm the sheet is opened from Mais ações, so that is where focus goes back to once it closes, if it was in the sheet.
	panelClosed() {
		if (!this.panelTarget.hidden || matchMedia("(min-width: 640px)").matches) return;
		const focused = document.activeElement;
		if (!focused || focused === document.body || focused === this.triggerTarget || this.panelTarget.contains(focused)) this.moreTarget.focus();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard;
	}

	get scene() {
		return this.board.state.view.peek().scene;
	}

	// Runs after menu#toggle: a menu that just opened starts on its list.
	opened() {
		if (this.panelTarget.hidden) return;
		this.show("menu");
		this.askTarget.disabled = true;
		this.load();
	}

	// Below sm the name is not in the bar: Mais ações opens this same menu, drawn as a bottom sheet.
	openSheet() {
		if (!this.panelTarget.hidden) return;
		this.triggerTarget.click();
		this.closeTarget.focus();
	}

	show(view: "menu" | "delete") {
		for (const element of this.viewTargets) element.hidden = element.dataset.view !== view;
	}

	// Escape leaves the question, not the menu.
	cancel(event: Event) {
		if (event instanceof KeyboardEvent) event.stopPropagation();
		this.show("menu");
		(this.closeTarget.offsetParent ? this.closeTarget : this.triggerTarget).focus();
	}

	// Excluir mapa waits for the list: its question names the map that takes the table.
	async load() {
		const load = ++this.loads;
		try {
			const response = await fetch(`${this.board.scenesUrlValue}.json`, { headers: { Accept: "application/json" }, credentials: "same-origin" });
			if (!response.ok) throw new Error(String(response.status));
			const { scenes } = await response.json();
			if (load !== this.loads) return;
			this.scenes = scenes;
			this.render();
			this.askTarget.disabled = false;
		} catch {
			if (load === this.loads) this.board.toast(this.labelsValue.failed);
		}
	}

	render() {
		const open = this.scene.id;
		this.listTarget.replaceChildren(
			...this.scenes.map((scene) => {
				const row = this.rowTarget.content.cloneNode(true) as DocumentFragment;
				const link = row.querySelector("a") as HTMLAnchorElement;
				const part = (name: string) => row.querySelector<HTMLElement>(`[data-part="${name}"]`) as HTMLElement;
				part("name").textContent = scene.name;
				// The version is for the cache alone: a master who just painted sees the new terrain.
				(part("thumb") as HTMLImageElement).src = `${this.board.scenesUrlValue}/${scene.id}/miniatura?v=${scene.version}`;
				if (!scene.active) part("table").remove();
				if (scene.id === open) link.setAttribute("aria-current", "page");
				else {
					part("open").remove();
					link.href = `${this.board.scenesUrlValue}/${scene.id}`;
				}
				return row;
			}),
		);
	}

	askDelete() {
		const { id, name, active } = this.scene;
		const next = active ? heir(this.scenes, id) : undefined;
		const { deleteBody, takesPlace, lastMap } = this.labelsValue;
		const after = active && this.scenes.length ? (next ? fill(takesPlace, next.name) : lastMap) : "";
		this.deleteBodyTarget.textContent = `${fill(deleteBody, name)} ${after}`.trim();
		this.show("delete");
		this.confirmTarget.focus();
	}

	// The scene's removal reaches this board over the cable and would send it to the list, so it is told it is leaving first.
	async confirmDelete() {
		const board = this.board;
		const { id } = this.scene;
		this.confirmTarget.disabled = true;
		board.leaving = true;
		let gone = false;
		try {
			const response = await fetch(`${board.scenesUrlValue}/${id}.json`, {
				method: "DELETE",
				headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
				credentials: "same-origin",
			});
			gone = response.ok || response.status === 404;
		} catch {
			// A lost response: the removal over the cable says whether the map is gone.
		}
		// The removal can reach the board first (over the cable, or as the 404 of a resync) and be held back by `leaving`, so a
		// map already gone leaves as well.
		if (gone || board.gone || !this.scene) return Turbo.visit(this.campaignUrlValue, { action: "replace" });
		board.leaving = false;
		this.confirmTarget.disabled = false;
		board.toast(this.labelsValue.failed);
	}
}
